import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Typography, Stack } from '@mui/material'
import {
  CommonButton,
  CommonSelect,
  CommonDatePicker,
} from '@/components/common'
import { useGetProcedureTypes, useGetDonorCenters } from '@/hooks'
import { useDonorContext } from '@/context'
import { ROUTES, DONOR_PORTAL_SCREEN_CONFIG_CODE } from '@/constants'
import {
  DonorPortalScreenMessage,
  getEligibilityDateHelperText,
} from '@/components/donation'
import {
  getSearchByDonorCentersParams,
  getSuggestedDonationDateFromProcedureEligibility,
  getEarliestSuggestedDonationDateFromEligibilities,
} from '@/utils'

export const SearchByDonorCenters = () => {
  const navigate = useNavigate()
  const { donor, getDonor } = useDonorContext()
  const { procedureTypes, loadingProcedureTypes, getProcedureTypes } =
    useGetProcedureTypes()
  const { donorCenters, loadingDonorCenters, getDonorCenters } =
    useGetDonorCenters()

  const [formData, setFormData] = useState({
    donor_center_id: '',
    procedure_type_id: '',
    date: null,
  })

  useEffect(() => {
    getProcedureTypes()
    getDonorCenters()
  }, [])

  useEffect(() => {
    getDonor()
  }, [getDonor])

  useEffect(() => {
    const pid = formData.procedure_type_id
    if (!donor?.procedure_eligibilities?.length) return
    const suggested = pid
      ? getSuggestedDonationDateFromProcedureEligibility(
          donor.procedure_eligibilities,
          pid
        )
      : getEarliestSuggestedDonationDateFromEligibilities(
          donor.procedure_eligibilities
        )
    if (!suggested) return
    setFormData(s => (s.date != null ? s : { ...s, date: suggested }))
  }, [donor?.procedure_eligibilities, formData.procedure_type_id])

  const donorCenterOptions = useMemo(
    () =>
      (donorCenters || []).map(dc => ({
        id: String(dc.id),
        name: dc.name,
      })),
    [donorCenters]
  )

  const handleSearch = () => {
    const params = getSearchByDonorCentersParams(formData)
    navigate(`${ROUTES.DONATE_DONOR_CENTERS_CALENDAR}?${params.toString()}`)
  }

  const isDisabled = useMemo(() => !formData.donor_center_id, [formData])

  return (
    <Stack spacing={2} sx={{ pb: 1 }}>
      <DonorPortalScreenMessage
        code={DONOR_PORTAL_SCREEN_CONFIG_CODE.SEARCH_BY_DONOR_CENTERS_SEARCH}
      />
      <Box>
        <Typography variant='subtitle1' sx={{ mb: 0.5 }}>
          Search donor center
        </Typography>
        <CommonSelect
          id='sdc-donor-center'
          label='Donor center'
          placeholder='Select a donor center'
          enableSort={false}
          options={donorCenterOptions}
          value={formData.donor_center_id}
          loading={loadingDonorCenters}
          onChange={v => setFormData(s => ({ ...s, donor_center_id: v || '' }))}
          required
        />
      </Box>

      <Box>
        <Typography variant='subtitle1' sx={{ mb: 0.5 }}>
          What do you want to donate?
        </Typography>
        <CommonSelect
          id='sdc-procedures'
          label='Procedures'
          placeholder='All Available'
          enableSort={false}
          options={procedureTypes}
          value={formData.procedure_type_id}
          loading={loadingProcedureTypes}
          onChange={v => {
            const procedure_type_id = v || ''
            setFormData(s => {
              const next = { ...s, procedure_type_id }
              if (!procedure_type_id) return next
              const suggested =
                getSuggestedDonationDateFromProcedureEligibility(
                  donor?.procedure_eligibilities,
                  procedure_type_id
                )
              if (suggested) next.date = suggested
              return next
            })
          }}
        />
      </Box>

      <Box>
        <Typography variant='subtitle1' sx={{ mb: 0.5 }}>
          Open calendar on (optional)
        </Typography>
        <CommonDatePicker
          id='sdc-date'
          label='Date'
          value={formData.date}
          onChange={d => setFormData(s => ({ ...s, date: d }))}
          helperText={getEligibilityDateHelperText({
            donorId: donor?.id,
            procedureEligibilities: donor?.procedure_eligibilities,
            procedureTypeId: formData.procedure_type_id,
            date: formData.date,
          })}
        />
      </Box>

      <Box pt={3} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <CommonButton
          id='sdc-cta'
          label='View Calendar'
          variant='primary'
          color='primary'
          size='large'
          disabled={isDisabled}
          onClick={handleSearch}
        />
      </Box>
    </Stack>
  )
}

export default SearchByDonorCenters
